import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faRefresh} from "@fortawesome/free-solid-svg-icons/faRefresh";
import {formatRuntimeDate} from "./runtimeStatusUtils.js";
import BrandLogo from "../../utils/brands/BrandLogo.jsx";
import {useBrand} from "../../utils/brands/branding.js";
import Badge from "../Badge.jsx";

function RuntimeInfoLine({label, value, danger = false}) {
    if (!value) {
        return null;
    }

    return (
        <div className={danger ? "text-rose-600 dark:text-rose-300" : undefined}>
            <span className={"font-bold text-slate-700 dark:text-slate-300"}>{label}: </span>
            <span>{value}</span>
        </div>
    );
}

export default function IntegrationRuntimeItem({
    integration,
    compact = false,
}) {
    const {t} = useTranslation();
    const lastFetchAt = formatRuntimeDate(integration.lastSuccessfulFetchAt);
    const accountName = integration?.accountName ?? null;
    const brand = useBrand(integration.integrationName);
    const isFetching = integration.status === "running" && integration.phase === "fetching";
    const isWaitingForPublish = integration.status === "running" && integration.phase === "waiting_for_publish";
    const connectionStatus = integration.ready
        ? t("runtime.integration.connection_active", {defaultValue: "aktivní"})
        : t("runtime.integration.connection_inactive", {defaultValue: "neaktivní"});
    const connectionStatusClass = integration.ready
        ? "text-emerald-600 dark:text-emerald-400"
        : "text-rose-600 dark:text-rose-300";

    return (
        <div className={"rounded-lg border bg-slate-50/80 dark:bg-slate-900/40 " + (compact ? "px-3 py-2" : "px-4 py-3")}>
            <div className={"min-w-0 flex-1"}>
                <div className={"flex flex-wrap items-center gap-2"}>
                    {brand && <BrandLogo brand={brand} size={6} className={"shrink-0"} />}
                    <div className={"font-semibold text-black dark:text-slate-100 mr-auto"}>
                        {integration.integrationName}
                    </div>

                    {isFetching && (
                        <Badge color={"yellow"} className={"gap-1.5 ml-auto"}>
                            <FontAwesomeIcon icon={faRefresh} className={"animate-spin"} />
                            <span>{t("runtime.integration.fetching_now", {defaultValue: "Stahuji data"})}</span>
                        </Badge>
                    )}

                    {isWaitingForPublish && (
                        <Badge color={"yellow"} className={"gap-1.5 ml-auto"}>
                            <FontAwesomeIcon icon={faRefresh} className={"animate-spin"} />
                            <span>{t("runtime.integration.waiting_for_publish", {defaultValue: "Čeká na zpracování projektu"})}</span>
                        </Badge>
                    )}
                </div>

                <div className={"mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-300"}>
                    <RuntimeInfoLine
                        label={t("runtime.integration.account", {defaultValue: "Účet"})}
                        value={accountName}
                    />
                    <RuntimeInfoLine
                        label={t("runtime.integration.last_update", {defaultValue: "Aktualizováno"})}
                        value={lastFetchAt}
                    />
                    <RuntimeInfoLine
                        label={t("runtime.integration.error", {defaultValue: "Chyba"})}
                        value={integration.error}
                        danger={true}
                    />
                    <div>
                        <span className={"font-bold text-slate-700 dark:text-slate-300"}>
                            {t("runtime.integration.status", {defaultValue: "Stav integrace"})}
                            {": "}
                        </span>
                        <span className={connectionStatusClass}>{connectionStatus}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
